import { connect, injectReducer, injectSaga } from '@vfde-sails/core';
import * as avcActions from 'Container/AvailabilityCheck/action';
import * as appActions from 'Container/App/action';
import { selectors as mapStateToProps } from 'Container/AvailabilityCheck/selector';
import {
    AddressHtmlId,
    ResultHtmlId,
    OverlayContent,
    FormId,
} from '../../enums/elementId';
import { AVC_NAMESPACE } from 'Container/App/constants';
import saga from './saga';
import avcReducer from './reducer';
import { showElement } from '../../util/visibility';
import { mountButtonLink } from '../../components/buttonLink';
import { getUpdateFlags } from './util/state';
import { updateFormElement, updateSelectFormElement } from '../../components/formElement';
import { mountLoadingSpinner, updateLoadingSpinner } from '../../components/loadingSpinner';
import { mountButton, updateAvcResultButton } from '../../components/button';
import { mountCaptcha } from '../../components/captcha';
import {
    mountNotification,
    mountNotificationServiceError,
    updateAvcNotification,
} from '../../components/notification';
import { mountText, updateAddressText, updateAlternativeText } from '../../components/justText';
import { mountFormSublineTooltip } from '../../components/tooltip';
import { formValidationHandleBtnDisable } from '@vfde-brix/ws10/validation/base/submitButtonHandler';
import { mountAvcForm } from './util/mountAvcForm';
import { isAddressError, isAddressNotFoundError } from './util/typeGuard';
import { addDisplayMetacodeToAvcResult } from '../../util/displayMetacodes';
import { HtmlSelector } from '../../constants';

const mapDispatch = {
    ...avcActions,
    ...appActions,
};

export type State = ReturnType<typeof mapStateToProps>;
export type Actions = typeof mapDispatch;

function container (state: State, actions: Actions) {
    injectSaga(AVC_NAMESPACE, saga);
    injectReducer(AVC_NAMESPACE, avcReducer);

    const avcFormInstance = mountAvcForm(actions);
    const { city, street, number, select, submit } = avcFormInstance;
    const spinner = mountLoadingSpinner(FormId.LOADING, true);
    const notification = mountNotification(ResultHtmlId.NOTIFICATION);
    const addressText = mountText(ResultHtmlId.CHECKED_ADDRESS);
    const alternativeText = mountText(ResultHtmlId.ALTERNATIVE_TEXT);
    const resultButton = mountButton(ResultHtmlId.BUTTON);

    mountNotificationServiceError(FormId.SERVICE_ERROR, HtmlSelector.AVC_ERROR_HOTLINE);
    mountFormSublineTooltip(FormId.TOOLTIP);
    mountButtonLink(AddressHtmlId.DELETE_ADDRESS, () => {
        actions.resetForm();
        actions.deleteAddress();
        actions.resetTariffsToPreAvc();
        actions.setAccessSupplier();
        actions.setAvailabilityIndicator();
    });
    mountCaptcha();

    actions.getEntryState();

    return {
        getDerivedStateFromProps(newState: State, oldState: State) {
            const {
                address,
                addresses,
                error,
                avcResult,
                avcForm,
                isLoading,
                selectedTariff,
                isContentEnabled,
                homesPassedType,
                accessSupplier,
                availabilityIndicator,
            } = newState;
            const update = getUpdateFlags(newState, oldState);
            const availabilityFiber = avcResult?.availability.fiber;
            const isPreFiber = (availabilityFiber || availabilityFiber === undefined) && isContentEnabled;

            update.isLoading && updateLoadingSpinner(spinner, isLoading);

            if (update.addressNotification) {
                updateSelectFormElement(select, addresses);
                showElement(FormId.SELECT_CONTAINER, 1 < addresses.length);
            }

            if (update.avcForm) {
                updateFormElement(city, avcForm.city);
                updateFormElement(street, avcForm.street);
                updateFormElement(number, avcForm.number);
            }

            update.button && formValidationHandleBtnDisable({
                formFields: [ city, street, number ],
                submitButton: submit,
                fieldsWithErrors: [],
            });

            if (update.isAvcResultChanged) {
                updateAvcNotification(
                    notification,
                    avcResult!,
                    isPreFiber,
                    homesPassedType,
                    accessSupplier,
                    availabilityIndicator
                );
                updateAddressText(addressText, avcResult!.address);
                updateAlternativeText(alternativeText, avcResult!);
                updateAvcResultButton(
                    resultButton,
                    avcResult!.selected.id,
                    isPreFiber,
                    avcResult!.fastest,
                    homesPassedType,
                );
                addDisplayMetacodeToAvcResult(selectedTariff?.id || '');

                const changeAddress = () => {
                    actions.openOverlay(OverlayContent.FORM);
                    actions.setAddresses([address!]);
                };
                mountButtonLink(AddressHtmlId.CHANGE_ADDRESS, changeAddress);
                mountButtonLink(ResultHtmlId.CHANGE_ADDRESS, changeAddress);
            }

            showElement(FormId.SERVICE_ERROR, Boolean(error && !isAddressError(error)));
            showElement(FormId.ADDRESS_ERROR, isAddressNotFoundError(error));
        },
    };
}

const Container = connect(mapStateToProps, mapDispatch)(container);

export default Container;
